<template>
  <div
    class="custom-attribute--selector"
    :class="{ 'in-modal-form': inModalForm }"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <woot-multiselect
      v-if="inModalForm"
      ref="multiselector"
      :value="attributesSelected"
      label="attribute_display_name"
      track-by="id"
      selected-label
      deselect-label=""
      :options="attributes"
      :allow-empty="true"
      :multiple="true"
      :searchable="true"
      :show-labels="false"
      :placeholder="placeholder"
      :close-on-select="false"
      :style="selectorPlaceholder"
      @hook:mounted="onMultiselectMounted"
      @input="onCustomAttributesChanges"
      @open="onOpen"
      @close="onClose"
    >
      <template #clear>
        <span class="material-symbols-outlined search-icon"> search </span>
      </template>
      <template #beforeList>
        <div class="top-list">
          <div
            class="flex flex-row items-center px-4 py-2 gap-2 cursor-pointer hover:bg-[#F0FDF4]"
            @click.prevent="addNewAttribute"
          >
            <span class="material-symbols-outlined text-green-500"> add </span>
            <span class="text-green-500 font-[500]">{{
              $t(`${currentSection}.FORM.ADD.TITLE`)
            }}</span>
          </div>
        </div>
      </template>
      <template slot="option" slot-scope="props">
        <div class="flex flex-row items-center">
          <div
            class="text-[#6B7280] text-sm w-[170px] overflow-hidden text-ellipsis pr-3 py-2.5 rtl:pr-0 rtl:pl-3"
          >
            {{ props.option.attribute_description }}
          </div>
          <div class="w-6 h-6 flex text-gray-400 items-center justify-center">
            •
          </div>
          <div class="text-[#164B35] pl-3 py-2.5 rtl:pr-3 rtl:pl-0">
            {{ props.option.attribute_display_name }}
          </div>
        </div>
      </template>
      <template #afterList>
        <div class="bottom-list">
          <div
            class="flex flex-row items-center cursor-pointer border-t border-[#9ca3af] hover:bg-[#F0FDF4]"
          >
            <div
              class="bg-green-500 text-white text-base px-4 py-2 w-full text-center"
              @click.prevent="onApplySelect"
            >
              {{ $t(`${currentSection}.APPLY_BUTTON_TEXT`) }}
            </div>
            <div
              class="bg-white text-gray-500 text-base px-4 py-2 w-full text-center"
              @click.prevent="onCancelSelect"
            >
              {{ $t(`${currentSection}.CANCEL_BUTTON_TEXT`) }}
            </div>
          </div>
        </div>
      </template>
    </woot-multiselect>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import attributeMixin from 'dashboard/mixins/attributeMixin';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import rtlMixin from 'shared/mixins/rtlMixin';

export default {
  mixins: [alertMixin, attributeMixin, rtlMixin],
  props: {
    attributeType: {
      type: String,
      default: 'conversation_attribute',
    },
    contactId: { type: Number, default: null },
  },
  data() {
    return {
      attributesSelected: null,
      lastAttributesSelected: null,
      newlyAttributesAdded: [],
      attributesRemoved: [],
      placeholder: null,
      dropdownOpened: false,
      search: '',
    };
  },
  computed: {
    selectorPlaceholder() {
      if (this.attributesSelected?.length || !this.dropdownOpened) return {};
      return {
        '--tags-before-content': `'${this.$t(
          `${this.currentSection}.ADD_A_SKILL`
        )}'`,
      };
    },
  },
  watch: {
    initCustomAttributes(newVal) {
      if (this.inModalForm) {
        this.attributesSelected = this.attributes.filter(attr =>
          Object.prototype.hasOwnProperty.call(newVal, attr.attribute_key)
        );
        this.lastAttributesSelected = this.attributesSelected;
      }
    },
  },
  mounted() {
    if (this.inModalForm) {
      this.attributesSelected = this.attributes.filter(attr =>
        Object.prototype.hasOwnProperty.call(
          this.initCustomAttributes,
          attr.attribute_key
        )
      );
    }
    this.lastAttributesSelected = this.attributesSelected;
    this.placeholder = this.$t(`${this.currentSection}.ADD_A_SKILL`);
  },
  methods: {
    async addAttribute(attribute) {
      try {
        const {
          attribute_key: attributeKey,
          attribute_display_type: attributeDisplayType,
          default_value: attributeDefaultValue,
        } = attribute;
        const isCheckbox = attributeDisplayType === 'checkbox';
        const defaultValue = isCheckbox ? false : attributeDefaultValue || null;
        if (this.attributeType === 'conversation_attribute') {
          await this.$store.dispatch('updateCustomAttributes', {
            conversationId: this.conversationId,
            customAttributes: {
              ...this.customAttributes,
              [attributeKey]: defaultValue,
            },
          });
        } else if (this.inModalForm) {
          this.$emit('addAttribute', attributeKey, defaultValue);
          bus.$emit(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, attributeKey);
          return;
        } else {
          await this.$store.dispatch('contacts/update', {
            id: this.contactId,
            custom_attributes: {
              ...this.customAttributes,
              [attributeKey]: defaultValue,
            },
          });
        }
        bus.$emit(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, attributeKey);
        this.showAlert(this.$t('CUSTOM_ATTRIBUTES.FORM.ADD.SUCCESS'));
      } catch (error) {
        const errorMessage =
          error?.response?.message ||
          this.$t('CUSTOM_ATTRIBUTES.FORM.ADD.ERROR');
        this.showAlert(errorMessage);
      }
    },
    removeAttribute(attribute) {
      this.$emit('removeAttribute', attribute.attribute_key);
    },
    addNewAttribute() {
      this.$router.push(
        `/app/accounts/${this.accountId}/settings/custom-attributes/list#agentSkill`
      );
    },
    onMultiselectMounted() {
      const el = this.$refs.multiselector.$el;
      const tags = el.querySelector('.multiselect__tags');
      const wrapper = el.querySelector('.multiselect__content-wrapper');
      const content = wrapper?.querySelector('.multiselect__content');
      const topList = content?.querySelector('.top-list');
      const bottomList = content?.querySelector('.bottom-list');
      const input = tags?.querySelector('.multiselect__input');
      const searchIcon = el.querySelector('.search-icon');
      if (!wrapper || !content || !topList || !bottomList || !input) return;

      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'multiselect__input-wrapper';
      inputWrapper.appendChild(input);
      if (searchIcon) inputWrapper.appendChild(searchIcon);
      wrapper.insertBefore(inputWrapper, wrapper.firstChild);
      wrapper.insertBefore(topList, inputWrapper.nextSibling);
      wrapper.append(bottomList);
    },
    closeSelector() {
      this.$refs.multiselector.$refs.inner.deactivate();
    },
    onOpen() {
      this.dropdownOpened = true;
      this.placeholder = this.$t(`${this.currentSection}.SEARCH_PLACEHOLDER`);
    },
    onClose() {
      this.dropdownOpened = false;
      this.attributesSelected = this.lastAttributesSelected;
      this.placeholder = this.$t(`${this.currentSection}.ADD_A_SKILL`);
    },
    onApplySelect() {
      this.lastAttributesSelected = this.attributesSelected;
      this.displayCustomAttributes();
      this.closeSelector();
    },
    onCancelSelect() {
      this.attributesSelected = this.lastAttributesSelected;
      this.closeSelector();
    },
    onCustomAttributesChanges(values) {
      const previous = this.lastAttributesSelected || [];

      this.newlyAttributesAdded = values.filter(
        v => !previous.some(p => p.id === v.id)
      );
      this.attributesRemoved = previous.filter(
        p => !values.some(v => v.id === p.id)
      );

      this.attributesSelected = values;

      if (!this.dropdownOpened) {
        this.displayCustomAttributes();
        this.lastAttributesSelected = this.attributesSelected;
      }
    },
    displayCustomAttributes() {
      this.newlyAttributesAdded.forEach(attribute =>
        this.addAttribute(attribute)
      );
      this.attributesRemoved.forEach(attribute =>
        this.removeAttribute(attribute)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-attribute--selector {
  width: 100%;
}

.error {
  color: var(--r-500);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
}
::v-deep {
  &.custom-attribute--selector {
    .multiselect {
      margin-bottom: 6px;
      .multiselect__tag {
        @apply my-0 border border-gray-500 bg-white text-gray-500;
      }

      .multiselect__tags::before {
        content: var(--tags-before-content, '');
        position: absolute;
        font-size: 16px;
        top: 10px;
        color: #1F2937;
      }

      .search-icon {
        @apply text-gray-500 absolute top-[14px] z-[50];
      }

      .multiselect__content-wrapper {
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .multiselect__content {
          overflow-y: auto;
          flex: 1;

          &::-webkit-scrollbar {
            width: 3px;
          }

          &::-webkit-scrollbar-track {
            background: transparent;
          }

          &::-webkit-scrollbar-thumb {
            @apply bg-gray-300 rounded-full;
          }

          &::-webkit-scrollbar-thumb:hover {
            @apply bg-gray-400;
          }
        }

        .multiselect__input-wrapper {
          @apply relative w-full p-1;
          .multiselect__input {
            border: 1px solid #D1D5DB;
            height: 44px !important;
            @apply rounded-[8px] static w-full;

            &::placeholder {
              color: #9CA3AF;
            }
          }
        }
      }
    }

    &[dir='ltr'] {
      .multiselect {
        .search-icon {
          @apply left-[16px];
        }

        .multiselect__tags::before {
          left: 13px;
        }

        .multiselect__input-wrapper {
          .multiselect__input {
            @apply pl-[44px];
          }
        }
      }
    }

    &[dir='rtl'] {
      .multiselect {
        .search-icon {
          @apply right-[16px];
        }

        .multiselect__tags::before {
          right: 13px;
        }

        .multiselect__input-wrapper {
          .multiselect__input {
            @apply pr-[44px];
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.custom-attribute--selector.in-modal-form {
  &,
  & > .label-wrap {
    display: inline;
    position: unset;
  }
  .label-wrap .dropdown-wrap {
    left: 0px;
  }
  .dropdown-pane {
    bottom: 30px;
    .items-start {
      height: auto;
    }
  }
}
</style>
